import { createContext, useContext, useEffect, useReducer, useState } from 'react';
import ajaxStudent from '../util/remote/ajaxStudent';
import ajaxReport from '../util/remote/ajaxReport';
import functions from '../util/functions';

const initialState = { 
    metaData: null,
    currentPage: 1,
    listing: null,
    mounted: false,
    profileInfo: null, 
    studentId: null, 
    feesBalance: null,  
    loadProfile: null,
    studentReports: null,
    studentContacts: null,
    studentAttachments: null,
    contactTypes: null,
    studentPopulation: null,
    alumniCount: null,
    classId: '0',
    residenceId: '0',
    searchText: '',
    streamId: '',
};

const reducer = (state, action) => {
    switch (action.type) {
      case 'CLEAR':
        return { initialState};  // Reset state completely
      default:
        return {...state, [action.type]: action.payload};
    }
};


const StudentContext = createContext();

export const StudentProvider = ({children}) =>{
  const [state, dispatch] = useReducer(reducer, initialState);
  const [makeDefaultContact, setMakeDefaultContact] = useState({ show: false, contact: null });
  const [deleteContact, setDeleteContact] = useState({ show: false, contact: null });
  const [deleteFile, setDeleteFile] = useState({ show: false, file: null });

  const [filterClass, setFilterClass] = useState(null);


   /**
   * Only call this if the getTransactions has already been called on mount by the AccountStatementPage
   */
  useEffect(()=>{
    if(state.mounted){
      getStudents();
    }
  }, [state.currentPage])


  const getStudents = async (refresh = false, classId = null) => {
    dispatch({ type: "mounted", payload: true });
    setFilterClass(classId);
    if (refresh) {
        dispatch({ type: "listing", payload: null });
        dispatch({ type: "metaData", payload: null });
        setCurrentPage(1);
    }
    const payload = {
        page: refresh ? 1 : state.currentPage,
        class_id: classId ?? state.classId,
        residence_id: state.residenceId,
        search_text: state.searchText,
        stream_id: state.streamId,
    };
    const server_response = await ajaxStudent.getStudents(payload);
    if (server_response.status === "OK") {
        const newTransactions = server_response.details.data;

        dispatch({
            type: "listing",
            payload: refresh ? newTransactions
                    : state.listing
                    ? [...state.listing, ...newTransactions]
                    : newTransactions
        });

        dispatch({ type: "metaData", payload: server_response.details.meta });
    } else {
        dispatch({ type: "listing", payload: "404" });
        dispatch({ type: "metaData", payload: "404" });
    }
 };

 const filter = async()=>{
    await getStudents(true, state.classId);
  }

  

 const setCurrentPage = (page)=>{
    dispatch({ type: 'currentPage', payload: page });
 }
  

  const loadProfile = async (studentId) => {
    dispatch({ type: 'studentId', payload: studentId });
    dispatch({ type: 'profileInfo', payload: null });
    dispatch({ type: 'feesBalance', payload: null });
    dispatch({ type: 'studentReports', payload: null });
    dispatch({ type: 'studentContacts', payload: null });
    dispatch({ type: 'studentAttachments', payload: null });

    const server_response = await ajaxStudent.getStudentInfo(studentId);
    if (server_response.status === "OK") {
        dispatch({ type: 'profileInfo', payload: server_response.details });
    } else {
        dispatch({ type: 'profileInfo', payload: "404" });
    }

    await getStudentReports(studentId);
    await getStudentContacts(studentId);
    await getStudentAttachments(studentId);
  }

  const getStudentReports = async (studentId) => {
    const server_response = await ajaxReport.fetchStudentReports({student_id: studentId, page: 1});
    if (server_response.status === "OK") {
        dispatch({ type: 'studentReports', payload: server_response.details.data });
    } else {
        dispatch({ type: 'studentReports', payload: "404" });
    }
  }

  const getContactTypes = async () => {
    if (state.contactTypes) {
        return state.contactTypes;
    }
    const server_response = await ajaxStudent.fetchContactTypo();
    if (server_response.status === "OK") {
        dispatch({ type: 'contactTypes', payload: server_response.details });
        return server_response.details;
    } else {
        dispatch({ type: 'contactTypes', payload: "404" });
        return "404";
    }
  }

  const getStudentContacts = async (studentId) => {
    const server_response = await ajaxStudent.fetchContacts({ student_id: studentId });
    if (server_response.status === "OK") {
        dispatch({ type: 'studentContacts', payload: server_response.details });
    } else {
        dispatch({ type: 'studentContacts', payload: "404" });
    }
  }

  const getStudentAttachments = async (studentId) => {
    const server_response = await ajaxStudent.fetchStudentFiles(studentId);
    if (server_response.status === "OK") {
        dispatch({ type: 'studentAttachments', payload: server_response.details });
    } else {
        dispatch({ type: 'studentAttachments', payload: "404" });
    }
  }

  const getStudentPopulation = async ()=>{
    if(functions.isObject(state.studentPopulation)){
      return
    }
    const server_response = await ajaxStudent.getStudentPopulation();
    if(server_response.status === "OK"){
      dispatch({ type: 'studentPopulation', payload: server_response.details});
    }else{
      dispatch({ type: 'studentPopulation', payload: '404'});
    }
  }

  const getStudentAlumniCount = async ()=>{
    if(state.alumniCount){
      return
    }
    const server_response = await ajaxStudent.fetchAlumniCount();
    if(server_response.status === "OK"){
      dispatch({ type: 'alumniCount', payload: server_response.details});
    }
  }


  return(
    <StudentContext.Provider value={{
      ...state,
      makeDefaultContact,
      deleteContact, 
      deleteFile,
      filterClass,
      dispatch,
      getStudents,
      loadProfile,
      getContactTypes,
      getStudentContacts,
      setMakeDefaultContact,
      setDeleteContact,
      getStudentAttachments, 
      setDeleteFile,
      getStudentPopulation,
      getStudentAlumniCount,
      setCurrentPage,
      setFilterClass,
      filter,
      }}>
      {children}
    </StudentContext.Provider>
  )

}

export const useStudent = ()=>useContext(StudentContext);

